import {
  AbstractMesh,
  Angle,
  ArcRotateCamera,
  BoundingInfo,
  Material,
  Mesh,
  PBRMaterial,
  SceneLoader,
  TransformNode,
  Vector3,
} from 'babylonjs'
import 'babylonjs-loaders'
import { IModel } from '../../type/modelType'
import { Context3D } from '../3d-context'
import { customLoadingUI } from '../commonLib/engine'
import { MissingModelError, MissingSceneError } from '../errors'

// NOTE: IMPORTANT UNDERSTANDING

// modelGroup & meshGroup
// Those 2 object above very important it use for query and manage model mesh.
// Mean all model mesh(object) will inside modelGroup and meshGroup.
// modelGroup use for add node purpose
// meshGroup is group of model mesh(object)

// NOTE: IMPORTANT UNDERSTANDING

// TODO: Have option for standard material and PBR material

export async function loadModel(model: IModel, context = Context3D.get()) {
  // Do something before load model
  if (!context.scene || !context.camera || !context.engine) {
    throw new MissingSceneError()
  }

  context.engine.displayLoadingUI()
  customLoadingUI()

  cleanupExistingModel()

  await loadModelProcess(model)
  loadConfiguration(model)
}

function cleanupExistingModel(context = Context3D.get()) {
  if (!context.scene) return

  // cleanup exsiting model
  const modelGroup = context.scene.getNodeByID('modelGroup')
  const meshGroup = context.scene.getMeshByID('meshGroup')
  if (meshGroup) meshGroup.dispose(false, true)
  if (modelGroup) modelGroup.dispose(false, true)
}

function loadModelProcess(model: IModel, context = Context3D.get()) {
  return new Promise((resolve, reject) => {
    if (!context.scene || !context.camera) {
      reject(false)
      return
    }

    const scene = context.scene
    const camera = context.camera

    // Extract filename and root url, use for load babylon model function
    const _fileName = model.source.match(/[^\/\?#]+(?=[^\/]*$)/g)
    if (!_fileName?.length) {
      reject(false)
      return
    }
    const fileName = _fileName[0]
    const rootUrl = model.source.replace(fileName, '')
    console.log('rootUrl fileName', rootUrl, fileName, model.source)

    // https://doc.babylonjs.com/divingDeeper/importers/loadingFileTypes
    // In babylon doc they have many way to load file and assets for difference purpose.
    // I use LoadAssetContainer because i want to modify mesh and material during process

    SceneLoader.LoadAssetContainer(
      rootUrl,
      fileName,
      scene,
      function (container) {
        // NOTE : MTL will autoload from OBJ file, so we need to make sure, OBJ has correct MTL link

        if (container.meshes.length <= 1) {
          reject(false)
          throw new MissingModelError()
        }

        const meshes = container.meshes
        const materials = container.materials
        console.log('LoadAssetContainer', container, meshes, materials)
        // container.addAllToScene()
        // return

        // Collect modify material
        // material in babylon can't direct access to props and also can't modify.
        // So i have to do this process first for modify stuff from MTL by create new material and assign current MTL material to it.
        const materialSET: Material[] = []

        materials.forEach(ma => {
          // In case material not PBR
          console.log('material type', ma.getClassName())
          if (ma.getClassName() === 'PBRMaterial') return

          // In case material not PBR
          // Create new material (cloning of material from MTL)
          const materialMod = new PBRMaterial(ma.name, scene)
          materialMod.roughness = 0.5
          materialMod.metallic = 0.5

          materialSET.push(materialMod)
        })

        // Load material and create by extend data and assign to mesh
        console.log('materialSET', materialSET)

        // CREATE GROUP
        // https://doc.babylonjs.com/typedoc/classes/babylon.transformnode
        // TransformNode GROUP
        const modelGroup = new TransformNode('modelGroup', scene, undefined)
        // Mesh Group > Purpose for make bounding box
        const meshGroup = new Mesh('meshGroup', scene)

        meshGroup.parent = modelGroup
        scene.addMesh(meshGroup)

        meshes.forEach(me => {
          // In case material not PBR
          const assignMat = materialSET.find(
            ma => ma.name === me.material?.name
          )
          if (assignMat) {
            me.material = assignMat
          } else if (me.material) {
            me.material.transparencyMode = 2 // Just change from Blender default
            // NOTE : If material not manual create (material from load) then need to addMaterial here to make it show on material list in inspector
            const checkDuplicateMat = scene.getMaterialByName(me.material.name)
            if (!checkDuplicateMat) scene.addMaterial(me.material)
          }

          meshGroup.addChild(me)

          // Necessary: After make group then need to assign each meshes from model to scene mesh
          // Why? Because group in BABYLONJS just for child > parent between node, not about grouping element
          scene.addMesh(me)
          // if ( me.material ) scene.addMaterial(me.material)
        })

        // **** RESET MODEL SCALE TO WORLD SCALE
        // So modelGroup.scaling will show actual scale after normalize
        // Increase scale by using current scale with multiply
        // https://doc.babylonjs.com/typedoc/classes/babylon.abstractmesh#normalizetounitcube
        modelGroup.normalizeToUnitCube()

        const scale = 50
        const scaleFactor = {
          x: modelGroup.scaling.x * scale,
          y: modelGroup.scaling.y * scale,
          z: modelGroup.scaling.z * scale,
        }
        modelGroup.scaling = new Vector3(
          scaleFactor.x,
          scaleFactor.y,
          scaleFactor.z
        )
        meshGroup.setBoundingInfo(totalBoundingInfo(meshGroup.getChildMeshes()))
        modelGroup.computeWorldMatrix(true)
        // meshGroup.showBoundingBox = true
        meshGroup.position.x -= meshGroup.getBoundingInfo().boundingBox.center.x
        meshGroup.position.y = 0 // Place on the floor at 0
        meshGroup.position.z -= meshGroup.getBoundingInfo().boundingBox.center.z

        setTimeout(() => {
          // TODO: Find proper way to set camera target, for now issue is bounding not fully update if call it instantly
          camera.setTarget(
            meshGroup.getBoundingInfo().boundingSphere.centerWorld
          ) // Look at model
          const centerWorld =
            meshGroup.getBoundingInfo().boundingBox.centerWorld
          console.log(centerWorld)
          camera.setTarget(
            new Vector3(centerWorld.x, centerWorld.y, centerWorld.z)
          ) // Look at model

          if (camera instanceof ArcRotateCamera) {
            camera.setPosition(new Vector3(0, 50, 50)) // Reset default
          }
          context.camera?.storeState()
        }, 500)

        if (model.transform?.rotate?.x)
          meshGroup.rotation.x = Angle.FromDegrees(
            model.transform?.rotate?.x
          ).radians()
        if (model.transform?.rotate?.y)
          meshGroup.rotation.y = Angle.FromDegrees(
            model.transform?.rotate?.y
          ).radians()
        if (model.transform?.rotate?.z)
          meshGroup.rotation.z = Angle.FromDegrees(
            model.transform?.rotate?.z
          ).radians()

        console.log(
          'meshGroup',
          meshGroup,
          meshGroup.getBoundingInfo(),
          meshGroup.getHierarchyBoundingVectors()
        )

        console.log(
          'AFTER ADD OBJ TO SCENE',
          scene,
          scene.meshes,
          scene.getNodes()
        )

        resolve(true)
      }
    )
  })
}

function loadConfiguration(model: IModel, context = Context3D.get()) {
  if (!context.scene) return

  model.meshes.forEach(mesh => {
    mesh.variants.forEach(variant => {
      if (!variant.default) {
        context.scene?.getMeshByID(variant.meshName)?.setEnabled(false)
      }
    })
  })
}

/**
 * Find and return minimum maximum value of object in scene
 */
export function totalBoundingInfo(
  meshes: AbstractMesh[],
  ignoreMeshes?: string[]
) {
  let boundingInfo = meshes[0].getBoundingInfo()
  let min = boundingInfo.boundingBox.minimumWorld
  let max = boundingInfo.boundingBox.maximumWorld

  for (let i = 1; i < meshes.length; i++) {
    if (!ignoreMeshes?.includes(meshes[i].name)) {
      boundingInfo = meshes[i].getBoundingInfo()
      min = Vector3.Minimize(min, boundingInfo.boundingBox.minimumWorld)
      max = Vector3.Maximize(max, boundingInfo.boundingBox.maximumWorld)
    }
  }

  return new BoundingInfo(min, max)
}

// TODO: Separate util model function

export function getModelMeshs(context = Context3D.get()) {
  if (!context.scene) return

  const meshGroup = context.scene.getMeshByID('meshGroup')
  console.log('getModelMeshs', meshGroup, meshGroup?.getChildMeshes())
  return meshGroup?.getChildMeshes()
}

export function getModelMaterial(context = Context3D.get()) {
  if (!context.scene) return

  const meshes = getModelMeshs()
  const materials: PBRMaterial[] = []
  meshes?.forEach(mesh => {
    if (mesh.material) {
      materials.push(mesh.material as PBRMaterial)
    }
  })

  console.log('getModelMaterial', materials)

  return materials
}

export function adjustModelOnFloor(y: number, context = Context3D.get()) {
  if (!context.scene) return

  const meshGroup = context.scene.getMeshByID('meshGroup')

  if (!meshGroup) return

  // mesh.showBoundingBox = true
  meshGroup.position.y = y
}
